/**
 * Draft Conflict Dialog
 * Displays when local and server drafts have conflicting changes
 */

import React from 'react';
import { AlertTriangle, Clock, Server, Laptop } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ConflictResult, MergeStrategy, formatFieldName, formatValueForDisplay } from '@/services/draftConflictService';

interface DraftConflictDialogProps {
  conflict: ConflictResult | null;
  isOpen: boolean;
  onResolve: (strategy: MergeStrategy) => void;
  onDismiss: () => void;
}

export const DraftConflictDialog: React.FC<DraftConflictDialogProps> = ({
  conflict,
  isOpen,
  onResolve,
  onDismiss,
}) => {
  if (!conflict || !conflict.hasConflict) return null;

  const { localVersion, serverVersion, conflictingFields } = conflict;
  if (!localVersion || !serverVersion) return null;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Draft Conflict Detected
          </AlertDialogTitle>
          <AlertDialogDescription>
            Your local changes conflict with a newer version saved on the server.
            Choose which version to keep.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Version Comparison */}
        <div className="grid grid-cols-2 gap-4 my-4">
          {/* Local Version */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Laptop className="h-4 w-4 text-blue-500" />
              <span className="font-medium">Your Version</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
              <Clock className="h-3 w-3" />
              {formatDate(localVersion.updatedAt)}
            </div>
            <ScrollArea className="h-32">
              <div className="space-y-1.5">
                {conflictingFields?.map((field) => (
                  <div key={field} className="text-sm">
                    <span className="text-muted-foreground">{formatFieldName(field)}:</span>{' '}
                    <span className="font-medium text-blue-600">
                      {formatValueForDisplay(localVersion.answers[field])}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Server Version */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Server className="h-4 w-4 text-green-500" />
              <span className="font-medium">Server Version</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
              <Clock className="h-3 w-3" />
              {formatDate(serverVersion.updatedAt)}
            </div>
            <ScrollArea className="h-32">
              <div className="space-y-1.5">
                {conflictingFields?.map((field) => (
                  <div key={field} className="text-sm">
                    <span className="text-muted-foreground">{formatFieldName(field)}:</span>{' '}
                    <span className="font-medium text-green-600">
                      {formatValueForDisplay(serverVersion.answers[field])}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Conflict Count */}
        <div className="flex items-center justify-center">
          <Badge variant="secondary">
            {conflictingFields?.length} conflicting {conflictingFields?.length === 1 ? 'field' : 'fields'}
          </Badge>
        </div>

        <AlertDialogFooter className="mt-4">
          <Button variant="outline" onClick={onDismiss}>
            Decide Later
          </Button>
          <Button
            variant="outline"
            onClick={() => onResolve('newest')}
            className="gap-1.5"
          >
            <Clock className="h-4 w-4" />
            Keep Newest
          </Button>
          <Button
            variant="secondary"
            onClick={() => onResolve('local')}
            className="gap-1.5"
          >
            <Laptop className="h-4 w-4" />
            Keep Mine
          </Button>
          <Button
            onClick={() => onResolve('server')}
            className="gap-1.5"
          >
            <Server className="h-4 w-4" />
            Use Server
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
